import { Palette } from '@lifeomic/chroma-react/styles';
import { defineMessages, IntlShape } from 'react-intl';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { getVariantType } from './utils';

const messages = defineMessages({
  needleHoverMessage: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.singleShortVariantNeedlePlot.hoverText',
    defaultMessage: `<b>Mutations: {yCoordinateAndCount}</b><br />{uniqueAaChange}`,
  },
});

interface Transcript {
  aminoAcidChange?: string;
  class?: string;
  name?: string;
  frequency?: number;
}

interface Item {
  cdsAALen?: string;
  pfamId?: string;
  pfamCode?: string;
  pfamStart?: string;
  pfamEnd?: string;
  pfamFull?: string;
}

interface BuildPlotProps {
  formatMessage?: IntlShape['formatMessage'];
  transcripts?: Array<Transcript>;
  items?: Array<Item>;
  colorOptions?: Array<string>;
  primaryTranscript?: Transcript;
}

interface Domain {
  name?: string;
  coord?: string;
  fullName?: string;
  color?: string;
}

interface PrimaryTranscript {
  x: number;
  name: string;
  hovertext: string;
  transcriptClass: string;
}

interface BuildPlotResult {
  x: Array<string | null>;
  y: Array<number | null>;
  mutationGroups: Array<string | null>;
  domains: Array<Domain>;
  needleHoverPointText: Array<string | null>;
  primary?: PrimaryTranscript | null;
}

/**
 * Converts data from the API into the format that the Needle Plot component
 * expects.
 */
export function buildPlot({
  formatMessage,
  transcripts,
  items,
  colorOptions = [],
  primaryTranscript,
}: BuildPlotProps): BuildPlotResult {
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  const x = [];
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  const y = [];
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  const mutationGroups = [];
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  let domains = [];
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  const needleHoverPointText = [];

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  if (!items.length) {
    return {
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      x,
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      y,
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      mutationGroups,
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      domains,
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      needleHoverPointText,
    };
  }

  // Find our first cdsAALen
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const cdsAALenValue = Number(items[0].cdsAALen) || 0;

  // We need to keep a dictionary for our counts:
  // - The key will be the number-block found from the amino acid change
  // - The value will be how many times that number-block appears
  //   in all of the transcripts
  const yCoordinateAndCount = {};
  // We also leverage other dictionaries for capturing the class and full
  // amino acid change props from the transcript.
  const yCoordinateClass = {};
  const yCoordinateAminoAcidChange = {};
  transcripts?.forEach(canonicalTranscript => {
    const {
      frequency,
      aminoAcidChange,
      class: transcriptClass,
    } = canonicalTranscript;

    if (!aminoAcidChange) {
      return;
    }

    const resultArrayOrNull = RegExp(/\d+/).exec(aminoAcidChange);

    if (!resultArrayOrNull) {
      return;
    }

    const [yValue] = resultArrayOrNull;

    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    if (yCoordinateAminoAcidChange[yValue]) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      yCoordinateAminoAcidChange[yValue].push(aminoAcidChange);
    } else {
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      yCoordinateAminoAcidChange[yValue] = [aminoAcidChange];
    }

    // "class" props are eventually pushed onto the mutation groups array
    if (transcriptClass) {
      const variantType = getVariantType(transcriptClass);
      if (
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        !yCoordinateClass[yValue] ||
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        (yCoordinateClass[yValue] &&
          // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
          variantType.priority <= yCoordinateClass[yValue].priority)
      ) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        yCoordinateClass[yValue] = {
          ...variantType,
          transcriptClass,
        };
      }
    }

    if (Object.prototype.hasOwnProperty.call(yCoordinateAndCount, yValue)) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      yCoordinateAndCount[yValue] =
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        yCoordinateAndCount[yValue] + (frequency || 1);
    } else {
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      yCoordinateAndCount[yValue] = frequency || 1;
    }
  });

  // Once we know how frequently our canonical transcript amino acid
  // change number-blocks appear, we can build our x and y values:
  // - The x-axis will start at 0, with a maximum length of the cdsAALen value + 1.
  //   The cdsAALen tells us how *long* our x-axis should be.
  // - The y-axis will be built by checking if we have any keys in our dictionary
  //   at the index.  If so, we will push the key's value onto the array; otherwise,
  //   we will push null.  Pushing nulls means it will not plot a point on the graph.
  // Above happens so that we can plot the needle pins.  A needle pin should
  // be placed any time the amino acid change is listed on the transcript array.
  for (let i = 0; i < cdsAALenValue + 1; i++) {
    if (Object.prototype.hasOwnProperty.call(yCoordinateAndCount, i)) {
      x.push(i.toString());
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      y.push(Math.log(yCoordinateAndCount[i]) + 1);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      mutationGroups.push(yCoordinateClass[i].transcriptClass);

      // We need to join the aa changes together
      const uniqueAaChanges = [
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        ...new Set(yCoordinateAminoAcidChange[i]),
      ] as string[];
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      let hoverText = formatMessage(messages.needleHoverMessage, {
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        yCoordinateAndCount: yCoordinateAndCount[i],
        uniqueAaChange: uniqueAaChanges[0],
      });
      if (uniqueAaChanges.length > 1) {
        uniqueAaChanges.shift();
        hoverText = `${hoverText}/${uniqueAaChanges
          .map((a: string) => {
            const index = a.indexOf(i.toString());
            return a.substr(index + i.toString().length);
          })
          .join('/')}`;
      }

      needleHoverPointText.push(hoverText);
    }
  }

  const availableColorOptions = [...colorOptions];
  const pfamIdToColor = {};
  colorOptions &&
    items?.forEach(item => {
      const { pfamId } = item;

      if (!Object.prototype.hasOwnProperty.call(pfamIdToColor, pfamId)) {
        // Get a unique color and add it to the list
        const color = availableColorOptions.shift();
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        pfamIdToColor[pfamId] = color;
      }
    });

  domains = buildColorCodedDomains({ colorOptions, pfamDataSets: items });

  let primary = null;
  if (primaryTranscript) {
    const resultArrayOrNull = RegExp(/\d+/).exec(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      primaryTranscript.aminoAcidChange,
    );
    if (resultArrayOrNull) {
      primary = {
        x: resultArrayOrNull[0],
        name: primaryTranscript.name,
        hovertext: `${primaryTranscript.aminoAcidChange} ${primaryTranscript.class}`,
        transcriptClass: primaryTranscript.class,
      };
    }
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  return { x, y, mutationGroups, domains, needleHoverPointText, primary };
}

/**
 * Builds the domains part of a Needle Plot with the provided color options
 * and pfam data sets.
 *
 * NOTE: A domain should share the same color on the graph if it shares
 * a pfamId.
 */
export const buildColorCodedDomains = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  colorOptions, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  pfamDataSets,
}) => {
  const availableColorOptions = [...colorOptions];
  const pfamIdToColor = {};
  colorOptions &&
    pfamDataSets?.forEach((item: any) => {
      const { pfamId } = item;

      if (!Object.prototype.hasOwnProperty.call(pfamIdToColor, pfamId)) {
        // Get a unique color and add it to the list
        const color = availableColorOptions.shift();
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        pfamIdToColor[pfamId] = color;
      }
    });

  // Mapping the "domains" is pretty straight forward, assuming the
  // pfamDataSets contain all of the required props (which they should!)
  return pfamDataSets
    .map((item: any) => {
      const { pfamCode, pfamId, pfamStart, pfamEnd } = item;

      if (!pfamCode || !pfamStart || !pfamEnd) {
        return null;
      }

      const domain: Domain = {
        name: item.pfamCode,
        coord: `${item.pfamStart}-${item.pfamEnd}`,
        fullName: item.pfamFull,
      };

      if (pfamId) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
        domain.color = pfamIdToColor[pfamId];
      }

      return domain;
    })
    .filter(Boolean);
};

/**
 * Generates a unique color palette for the Needle Plot color coded domain
 * regions.
 */
export const generateColors = ({ palette }: { palette: Palette }) => {
  const colors = [];

  for (let i = 1; i < 9; i++) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    colors.push(palette.green[i * 100]);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    colors.push(palette.blue[i * 100]);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    colors.push(palette.red[i * 100]);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    colors.push(palette.yellow[i * 100]);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    colors.push(palette.purple[i * 100]);
  }

  return colors;
};
